import {
  Box,
  Heading,
  Text,
  VStack,
  ListItem,
  Divider,
  Link as ChakraLink,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  OrderedList,
  UnorderedList,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import BasePageLayout from '@/components/layout/BasePageLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { StepContent } from './HelpStepContent'

export default function UserManualPage() {
  const { textSecondary, border } = useThemeColors()

  return (
    <BasePageLayout
      mainBoxProps={{
        p: 4,
        h: 'full',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <VStack align="stretch" spacing={6} w="full" maxW="1200px">
        <Box>
          <Heading size="lg" mb={2}>
            User Manual
          </Heading>
          <Text color={textSecondary}>
            How to use Auth & Identity day to day. For troubleshooting when something goes wrong, see the{' '}
            <ChakraLink as={RouterLink} to="/faq" color="blue.500">
              FAQ
            </ChakraLink>
            .
          </Text>
        </Box>

        {/* Getting started — detailed accordion */}
        <Box>
          <Heading size="md" mb={2}>
            Getting started
          </Heading>
          <Text color={textSecondary} mb={4}>
            Follow these steps the first time you use Auth & Identity. Expand a step for full detail and screenshots.
            Collapse steps you already know.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. Open the Auth & Identity login page
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/01-login.png',
                    alt: 'Auth & Identity login page',
                    caption: 'Login screen: Email, Password, Remember me, Forgot your password?, and Sign In.',
                  }}
                >
                  <Text>
                    Open Auth & Identity in your browser (your organization&apos;s Auth URL, or the local address your
                    team uses such as <Text as="span" fontFamily="mono" fontSize="sm">http://localhost:3000</Text>).
                    You should land on the login screen titled <strong>Auth & Identity</strong>.
                  </Text>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>Confirm you see the ActionLabs logo and the heading <strong>Auth & Identity</strong>.</ListItem>
                    <ListItem>If you were already signed in, you may go straight to the dashboard — you can skip to step 7.</ListItem>
                    <ListItem>If the page does not load, check your network or ask IT for the correct URL.</ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Enter your email and password
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/01-login.png',
                    alt: 'Login form email and password fields',
                    caption: 'Use the Email and Password fields on the login card. Eye icon shows or hides the password.',
                  }}
                >
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      In the <strong>Email</strong> field (envelope icon), type the email address registered for your
                      ActionLabs account.
                    </ListItem>
                    <ListItem>
                      In the <strong>Password</strong> field (padlock icon), type your password.
                    </ListItem>
                    <ListItem>
                      Optional: click the <strong>eye</strong> icon on the right of the password field to show or hide
                      what you typed, so you can check for typos.
                    </ListItem>
                    <ListItem>Do not use someone else&apos;s account. Each person should sign in with their own credentials.</ListItem>
                  </OrderedList>
                  <Text color={textSecondary} fontSize="sm">
                    If the form highlights a field in red, fix the validation message (for example, empty email or
                    invalid email format) before continuing.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Choose Remember me (optional)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/01-login.png',
                    alt: 'Remember me checkbox on login',
                    caption: 'Remember me sits under the password field, left of Forgot your password?.',
                  }}
                >
                  <Text>
                    Below the password field, on the left, you will see a <strong>Remember me</strong> checkbox.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Checked</strong> — keep your session longer on this device (useful on a personal or
                      trusted computer). Idle session warnings may behave differently when Remember me is on.
                    </ListItem>
                    <ListItem>
                      <strong>Unchecked</strong> — prefer this on shared or public computers so the session ends more
                      readily when you leave.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Forgot your password? (optional recovery)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/02-forgot-password.png',
                    alt: 'Forgot password page',
                    caption: 'Forgot Password?: enter your email, Send Reset Link, or go Back to Login.',
                  }}
                >
                  <Text>
                    If you cannot sign in because you forgot your password, use recovery before trying many wrong
                    passwords (that can lock the account depending on security policy).
                  </Text>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      On the login screen, click <strong>Forgot your password?</strong> (blue link on the right under
                      the password field).
                    </ListItem>
                    <ListItem>
                      On the <strong>Forgot Password?</strong> page, enter the same email address you use to sign in.
                    </ListItem>
                    <ListItem>
                      Click <strong>Send Reset Link</strong>. Check your inbox (and spam/junk) for the reset email.
                    </ListItem>
                    <ListItem>
                      Open the link in the email, set a new password, then return to login with the new password.
                    </ListItem>
                    <ListItem>
                      Use <strong>Back to Login</strong> if you opened this page by mistake.
                    </ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Click Sign In
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/01-login.png',
                    alt: 'Sign In button on login page',
                    caption: 'Click the blue Sign In button at the bottom of the login card.',
                  }}
                >
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      Click the blue <strong>Sign In</strong> button at the bottom of the login card.
                    </ListItem>
                    <ListItem>Wait for the button to finish loading. Do not click repeatedly.</ListItem>
                  </OrderedList>
                  <Text fontWeight="semibold">What happens next depends on your account:</Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Signed in successfully</strong> — you go to the <strong>Dashboard</strong>. Continue at
                      step 8.
                    </ListItem>
                    <ListItem>
                      <strong>2FA setup required</strong> — your organization requires two-factor authentication and
                      your account is not enrolled yet. Continue at step 6.
                    </ListItem>
                    <ListItem>
                      <strong>2FA verification required</strong> — a code was sent to your email. Continue at step 7.
                    </ListItem>
                    <ListItem>
                      <strong>Error message</strong> — wrong email/password or account issue. Correct the credentials
                      or use Forgot password / contact an administrator.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. First-time 2FA setup (only if required)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/04-two-factor-setup.png',
                    alt: 'Two-factor authentication setup page',
                    caption: '2FA setup: re-enter password, then Enable Two-Factor Authentication.',
                  }}
                >
                  <Text>
                    You only see this screen when an administrator has made two-factor authentication mandatory and
                    your account has not enabled it yet. The title is{' '}
                    <strong>Two-Factor Authentication Required</strong>.
                  </Text>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>Confirm the email shown on the page is your account email.</ListItem>
                    <ListItem>
                      Re-enter your <strong>Password</strong> in the field (this confirms it is really you before
                      enabling 2FA).
                    </ListItem>
                    <ListItem>
                      Click <strong>Enable Two-Factor Authentication</strong>.
                    </ListItem>
                    <ListItem>
                      After success, you are sent to the verification step (step 7) and a 6-digit code is emailed to
                      you.
                    </ListItem>
                    <ListItem>
                      Use <strong>Back to Login</strong> only if you need to start over with a different account.
                    </ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    7. Enter your email verification code (2FA)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/03-two-factor.png',
                    alt: 'Two-factor authentication verification page',
                    caption: '2FA verify: enter the 6-digit email code, Verify Code, or Resend Code.',
                  }}
                >
                  <Text>
                    On the <strong>Two-Factor Authentication</strong> page, Auth & Identity has sent a{' '}
                    <strong>6-digit verification code</strong> to your email. The page shows which address was used.
                  </Text>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>Open your email inbox for that address (check spam/junk if needed).</ListItem>
                    <ListItem>
                      Find the ActionLabs verification message and copy the 6-digit code.
                    </ListItem>
                    <ListItem>
                      Type the code into <strong>Verification Code</strong>. Digits only; the field accepts 6 numbers.
                      When all 6 digits are entered, verification may submit automatically.
                    </ListItem>
                    <ListItem>
                      Or click <strong>Verify Code</strong> if it does not submit on its own.
                    </ListItem>
                    <ListItem>
                      Watch <strong>Verification Attempts</strong> (for example <Text as="span" fontFamily="mono" fontSize="sm">0 / 3</Text>
                      ). You have a limited number of tries — after too many failures you may need to wait or start
                      again from login.
                    </ListItem>
                    <ListItem>
                      If no email arrives, click <strong>Resend Code</strong> (there may be a short cooldown), then
                      wait for a new message.
                    </ListItem>
                    <ListItem>
                      Use <strong>Back to Login</strong> if you need to use a different account.
                    </ListItem>
                  </OrderedList>
                  <Text color={textSecondary} fontSize="sm">
                    After a successful code, you are taken to the dashboard.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    8. Find your way around after you sign in
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/05-after-signin.png',
                      alt: 'Auth & Identity after sign-in showing sidebar, header, and pinned help links',
                      caption:
                        'After sign-in: sidebar (modules + pinned User Manual / FAQ), header (theme, notifications, avatar), and page content.',
                    },
                    {
                      src: '/assets/img/help/06-profile-menu.png',
                      alt: 'User menu open with Profile and Logout',
                      caption: 'Click your name / avatar to open Profile and Logout.',
                    },
                  ]}
                >
                  <Text>
                    Once signed in, Auth & Identity shows the main application shell: a sidebar on the left, a header
                    on top, and the page content in the center.
                  </Text>

                  <Heading as="h4" size="sm">
                    Sidebar (left)
                  </Heading>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      Use the <strong>logo</strong> area at the top to recognize you are in Auth & Identity.
                    </ListItem>
                    <ListItem>
                      Scroll the middle list for modules your role can access (for example User Management,
                      Organization, Settings). Click a group to expand it, then click a child item to open that page.
                    </ListItem>
                    <ListItem>
                      At the <strong>bottom</strong> of the sidebar you always see <strong>User Manual</strong> and{' '}
                      <strong>FAQ</strong> — available to every signed-in user (this page).
                    </ListItem>
                    <ListItem>
                      On a narrow screen, open the menu with the header <strong>menu (hamburger)</strong> button; the
                      same links appear in a drawer, including User Manual and FAQ at the bottom.
                    </ListItem>
                  </OrderedList>

                  <Heading as="h4" size="sm">
                    Header (top)
                  </Heading>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      The title and breadcrumbs show where you are (for example Home / User Management / Users).
                    </ListItem>
                    <ListItem>
                      Use the <strong>moon/sun</strong> icon to switch light and dark theme.
                    </ListItem>
                    <ListItem>
                      Click your <strong>name / avatar</strong> on the right to open the user menu.
                    </ListItem>
                    <ListItem>
                      Choose <strong>Profile</strong> to update your name, password, or 2FA settings for your own
                      account.
                    </ListItem>
                    <ListItem>
                      Choose <strong>Logout</strong> when you finish, especially on a shared computer.
                    </ListItem>
                  </OrderedList>

                  <Heading as="h4" size="sm">
                    What you can open
                  </Heading>
                  <Text>
                    Menus depend on your <strong>role permissions</strong>. If a teammate sees a page you do not, ask
                    an administrator to adjust your role — then sign out and sign in again so navigation refreshes. For
                    common problems, open the{' '}
                    <ChakraLink as={RouterLink} to="/faq" color="blue.500">
                      FAQ
                    </ChakraLink>
                    .
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* User Management — detailed accordion */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            User Management
          </Heading>
          <Text color={textSecondary} mb={4}>
            Manage accounts, roles, devices, and activity. Expand a topic for steps and field-by-field form details.
            Menu items depend on your role permissions.
          </Text>

          <Accordion allowMultiple defaultIndex={[0, 1]} reduceMotion>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. What User Management covers
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <VStack align="stretch" spacing={3} maxW="720px">
                  <Text>
                    Open the <strong>User Management</strong> group in the sidebar. Typical pages (names may vary by
                    deployment):
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Users</strong> — create and maintain accounts (identity, org placement, apps, password,
                      status).
                    </ListItem>
                    <ListItem>
                      <strong>Roles</strong> — permission sets that control which menus and actions each user sees.
                    </ListItem>
                    <ListItem>
                      <strong>Assigned Devices</strong> — hardware registered to a person (laptop, phone asset, etc.).
                    </ListItem>
                    <ListItem>
                      <strong>Mobile Devices</strong> — phones/tablets used with the field app (force logout /
                      deactivate).
                    </ListItem>
                    <ListItem>
                      <strong>User Activity</strong> — timeline, login history, sessions, and statistics for one
                      account (opened from the Users list).
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    List pages usually share All / Trash tabs, search, bulk actions, and Create when you have
                    permission — same pattern as Organization.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Users (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/um-01-users.png',
                      alt: 'Users list page',
                      caption:
                        'Users list: All / Trash, search, bulk actions, export/import, Create New User, row actions.',
                    },
                    {
                      src: '/assets/img/help/um-02-create-user.png',
                      alt: 'Create New User form with all fields',
                      caption:
                        'Create New User: identity, organization, role/client, web apps & auditor, password, status.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Users</strong> → <strong>Create New User</strong> (or Edit a row). Complete the form,
                    then <strong>Create User</strong> / <strong>Save</strong>, or <strong>Cancel</strong> to return to
                    the list.
                  </Text>

                  <Heading as="h4" size="sm">
                    Identity &amp; contact
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Username / Client ID *</strong> — Unique login name. For client-style accounts this is
                      often the client code; for staff it is usually a short username (for example{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        j.santos
                      </Text>
                      ).
                    </ListItem>
                    <ListItem>
                      <strong>Email *</strong> — Work email for sign-in, password reset, and 2FA codes. Must be unique.
                    </ListItem>
                    <ListItem>
                      <strong>Employee ID</strong> — Optional HR / payroll ID (for example{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        ALS-25-520
                      </Text>
                      ). Useful for search and reports.
                    </ListItem>
                    <ListItem>
                      <strong>First Name *</strong> / <strong>Last Name *</strong> — Display name on lists, activity,
                      and the header avatar menu.
                    </ListItem>
                    <ListItem>
                      <strong>Position</strong> — Job title (for example Support Specialist). Descriptive only; does
                      not grant permissions.
                    </ListItem>
                    <ListItem>
                      <strong>Vicidial Agent Username</strong> — Only if this person uses Vicidial (for example Call
                      Requestor). Leave blank when not applicable.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Organization placement
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Department</strong> — Optional unit from Organization → Departments. Groups people; does
                      not replace Role permissions.
                    </ListItem>
                    <ListItem>
                      <strong>Area Cluster</strong> — Geographic / operational cluster (for example NCR North). Assign
                      when field coverage or routing depends on area.
                    </ListItem>
                    <ListItem>
                      <strong>Reports To (Supervisor)</strong> — Type at least 3 characters to search users, then pick
                      the immediate supervisor. Use <strong>Clear</strong> to remove. Builds the org hierarchy;
                      optional.
                    </ListItem>
                    <ListItem>
                      <strong>Team Assignment</strong> — Multi-select teams (Organization → Teams). Open the dropdown
                      and tick one or more. If none exist, configure teams under Organization first.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Access control
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Role *</strong> — Permission set that controls which sidebar menus and buttons appear.
                      Choose carefully. (“Developer Account” is not offered in this dropdown.)
                    </ListItem>
                    <ListItem>
                      <strong>Client *</strong> — Appears only when Role is <strong>Client</strong>. Select which
                      retail/tenant client this login represents. Required for Client role; hidden for other roles.
                    </ListItem>
                    <ListItem>
                      <strong>Web App Access &amp; Auditor</strong> — For each registered web application (System
                      Settings → Application):
                      <UnorderedList mt={2} spacing={1}>
                        <ListItem>
                          Tick the <strong>app name</strong> to allow this user to open that product.
                        </ListItem>
                        <ListItem>
                          Optionally tick <strong>Auditor – View &amp; Edit</strong> under that app (only enabled when
                          the app access box is checked).
                        </ListItem>
                      </UnorderedList>
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Password &amp; status
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Password *</strong> (required on create) — Click <strong>Generate Password</strong> or
                      type one. Must be at least 8 characters and include uppercase, lowercase, a digit, and a special
                      character (
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        @$!%*#?&amp;
                      </Text>
                      ). On <strong>edit</strong>, leave blank to keep the current password.
                    </ListItem>
                    <ListItem>
                      <strong>Status</strong> — <strong>Active</strong> can sign in; <strong>Inactive</strong> and{' '}
                      <strong>Suspended</strong> cannot. Use Suspended for temporary lockouts without deleting the
                      account.
                    </ListItem>
                  </UnorderedList>

                  <Text color={textSecondary} fontSize="sm">
                    After create/save, the user appears on the Users list. They still need a working email (and 2FA if
                    enabled) to complete first sign-in.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Bulk actions, import, and export
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/um-01-users.png',
                    alt: 'Users toolbar with bulk actions and export import',
                    caption: 'Toolbar: Bulk actions + Apply, Search, template / Import / Export, Create New User.',
                  }}
                >
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>Select one or more rows with the checkboxes.</ListItem>
                    <ListItem>
                      Choose a <strong>Bulk action</strong> (for example Delete, or Send password reset on All; Restore
                      / Delete Permanently on Trash), then click <strong>Apply</strong> and confirm the dialog.
                    </ListItem>
                    <ListItem>
                      Use <strong>Export</strong> to download CSV of the current list (or trash when on that tab).
                    </ListItem>
                    <ListItem>
                      Use <strong>Download template</strong> then <strong>Import</strong> to upload a CSV when you have
                      import permission.
                    </ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Roles (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/um-03-roles.png',
                      alt: 'Roles list page',
                      caption: 'Roles list: All / Trash, search, bulk actions, Create New Role.',
                    },
                    {
                      src: '/assets/img/help/um-04-create-role.png',
                      alt: 'Create New Role with permission checkboxes',
                      caption:
                        'Create New Role: Name, Active, and View / Create / Edit / Delete / Export / Import per menu.',
                    },
                  ]}
                >
                  <Text>
                    Roles control which menus and actions appear. Open <strong>Roles</strong> →{' '}
                    <strong>Create New Role</strong> (or Edit a row).
                  </Text>

                  <Heading as="h4" size="sm">
                    Form fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name *</strong> — Short unique label shown when assigning a role on the Users form (for
                      example Support Agent, Field Supervisor).
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Active roles can be assigned; turn off to retire a role without deleting
                      it.
                    </ListItem>
                    <ListItem>
                      <strong>Permissions *</strong> — Expand each navigation group. For every page, tick the
                      capabilities this role should have:
                      <UnorderedList mt={2} spacing={1}>
                        <ListItem>
                          <strong>View</strong> — Open the page / read data.
                        </ListItem>
                        <ListItem>
                          <strong>Create</strong> — Add new records (Create buttons).
                        </ListItem>
                        <ListItem>
                          <strong>Edit</strong> — Change existing records.
                        </ListItem>
                        <ListItem>
                          <strong>Delete</strong> — Soft-delete or remove records.
                        </ListItem>
                        <ListItem>
                          <strong>Export</strong> — Download CSV / files.
                        </ListItem>
                        <ListItem>
                          <strong>Import</strong> — Upload CSV / templates.
                        </ListItem>
                      </UnorderedList>
                      Parent checkboxes can select or clear a permission for all child pages under that group.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    After Create Role / Save, assign the role on Users. Affected users may need to sign out and sign in
                    again so navigation refreshes.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Assigned Devices (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/um-05-assigned-devices.png',
                      alt: 'Assigned Devices list',
                      caption: 'Assigned Devices list: user, model, brand, serial, OS, and status.',
                    },
                    {
                      src: '/assets/img/help/um-08-create-assigned-device.png',
                      alt: 'Create Assigned Device form',
                      caption:
                        'Create Assigned Device: Assigned User, Serial No, Android ID, Model, Brand, OS, RAM, Storage, Active.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Assigned Devices</strong> → <strong>Create New Device</strong> (or Edit) to register
                    hardware tied to users.
                  </Text>

                  <Heading as="h4" size="sm">
                    Form fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Assigned User *</strong> — Type 3+ characters and pick the person who holds the device
                      (search by name, employee ID, or email).
                    </ListItem>
                    <ListItem>
                      <strong>Serial No *</strong> — Unique serial number from the hardware label.
                    </ListItem>
                    <ListItem>
                      <strong>Android ID</strong> — Optional device Android ID when the asset is an Android unit.
                    </ListItem>
                    <ListItem>
                      <strong>Model *</strong> / <strong>Brand *</strong> — Hardware model and manufacturer (for
                      example ThinkPad T14 / Lenovo).
                    </ListItem>
                    <ListItem>
                      <strong>OS</strong> / <strong>OS Version</strong> — Operating system name and version number.
                    </ListItem>
                    <ListItem>
                      <strong>RAM Size</strong> / <strong>Storage Size</strong> — Optional capacity notes (for example
                      16GB / 512GB).
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — When off, the device is inactive and hidden from selection elsewhere.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Use bulk actions, export, or import when updating many devices at once (if your role allows).
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. Mobile Devices
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/um-06-mobile-devices.png',
                    alt: 'Mobile Devices list',
                    caption:
                      'Mobile Devices: name, platform, model, OS/app version, and status for field-app devices.',
                  }}
                >
                  <Text>
                    Open <strong>Mobile Devices</strong> to review phones/tablets used with the field app. This page is
                    mostly operational (no create form like Assigned Devices).
                  </Text>
                  <Heading as="h4" size="sm">
                    List columns &amp; actions
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name</strong> — User / device display name from the field app registration.
                    </ListItem>
                    <ListItem>
                      <strong>Platform</strong> / <strong>Model</strong> — Android or iOS and the device model.
                    </ListItem>
                    <ListItem>
                      <strong>Version</strong> / <strong>App Version</strong> — OS version and installed field-app
                      version.
                    </ListItem>
                    <ListItem>
                      <strong>Status</strong> — Whether the device session is Active.
                    </ListItem>
                    <ListItem>
                      Row actions such as <strong>Force Logout</strong> or <strong>Deactivate</strong> cut off access
                      (confirm the prompt). Open details when you need full device identity.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    7. User Activity
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/um-07-user-activity.png',
                    alt: 'User Activity with Timeline and other tabs',
                    caption:
                      'User Activity: Timeline, Login History, Active Sessions, and Statistics for one account.',
                  }}
                >
                  <Text>
                    From the Users list, open a user’s activity (eye / activity action on the row).
                  </Text>
                  <Heading as="h4" size="sm">
                    Tabs
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Timeline</strong> — Recent account actions and profile changes.
                    </ListItem>
                    <ListItem>
                      <strong>Login History</strong> — Past successful (and failed) sign-ins when recorded.
                    </ListItem>
                    <ListItem>
                      <strong>Active Sessions</strong> — Current sessions; useful when investigating concurrent access.
                    </ListItem>
                    <ListItem>
                      <strong>Statistics</strong> — Summary counts for auditing access patterns.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    For common problems after creating users or changing roles, see the{' '}
                    <ChakraLink as={RouterLink} to="/faq" color="blue.500">
                      FAQ
                    </ChakraLink>
                    .
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* Organization — detailed accordion */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            Organization
          </Heading>
          <Text color={textSecondary} mb={4}>
            Maintain clients, locations, departments, teams, field staff, and related catalogs. Expand a topic for
            steps and field-by-field form details. Menu items depend on your role.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]} reduceMotion>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. What Organization covers
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <VStack align="stretch" spacing={3} maxW="720px">
                  <Text>
                    Open the <strong>Organization</strong> group in the sidebar. Typical pages (names may vary by
                    deployment):
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Clients</strong>, <strong>Business Units</strong>, <strong>Stores</strong> — tenant and
                      branch structure. From a client row you can open{' '}
                      <strong>Manage Asset Catalog</strong> (per-client brands, categories, specs).
                    </ListItem>
                    <ListItem>
                      <strong>Area Clusters</strong>, <strong>Geographic Regions</strong>, <strong>Provinces</strong>,{' '}
                      <strong>Towns</strong> — location master data used by stores and field coverage.
                    </ListItem>
                    <ListItem>
                      <strong>Departments</strong>, <strong>Teams</strong> — org units used on user forms and routing.
                    </ListItem>
                    <ListItem>
                      <strong>Field Engineers</strong>, <strong>Agent Skills</strong>,{' '}
                      <strong>Client Purchase Orders</strong> — field staff, ticket skills, and contracts.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    List pages usually share All / Trash tabs, search, bulk actions, and Create when you have
                    permission — same pattern as User Management.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Clients (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-01-clients-list.png',
                      alt: 'Clients list page',
                      caption: 'Clients list: Code, Domain, Name, Database, POs, Status, Create Client.',
                    },
                    {
                      src: '/assets/img/help/org-01-clients-create.png',
                      alt: 'Create Client form with all fields',
                      caption:
                        'Create Client form: Name, Code, Email Domains (Domain / Type / Primary), Contact Emails (Exact email), Description, Status.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Clients</strong> → <strong>Create</strong> (or Edit a row). Clients are retail /
                    tenant records used across Auth, Support, and SIMA.
                  </Text>
                  <Heading as="h4" size="sm">
                    Form fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Client Name *</strong> — Display name (for example Jollibee Foods Corporation).
                    </ListItem>
                    <ListItem>
                      <strong>Client Code *</strong> — Short unique code (uppercase letters, numbers, underscores).
                      Used in URLs, databases, and client logins. <strong>Cannot be changed after create</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Email Domains</strong> — Add one or more rows. Each row has:
                      <UnorderedList mt={2} spacing={1}>
                        <ListItem>
                          <strong>Domain</strong> — for example demoretail.com or a platform domain.
                        </ListItem>
                        <ListItem>
                          <strong>Type</strong> — Corporate (direct match), Ticketing platform (needs keywords), or MSP /
                          shared (needs keywords).
                        </ListItem>
                        <ListItem>
                          <strong>Primary</strong> — mark the main domain for this client.
                        </ListItem>
                      </UnorderedList>
                      Use <strong>Add domain</strong> / remove as needed. Shared/platform types need subject keywords.
                    </ListItem>
                    <ListItem>
                      <strong>Contact Emails</strong> — Each row is an <strong>Exact email</strong> allowlist address
                      (for example premier@demoretail.com). Use <strong>Add contact email</strong> for more.
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — Optional notes (max 1000 characters).
                    </ListItem>
                    <ListItem>
                      <strong>Status</strong> — Active clients can be used by users and workflows; Inactive hides the
                      client from normal use.
                    </ListItem>
                    <ListItem>
                      If the SIMA database is missing, editors see <strong>Create Database</strong> (provisioning) in the
                      client list row menu and on the edit form. The action hides once the database is provisioned.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Business Units (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-02-business-units-list.png',
                      alt: 'Business Units list page',
                      caption: 'Business Units list with Create Business Unit.',
                    },
                    {
                      src: '/assets/img/help/org-02-business-units-create.png',
                      alt: 'Create Business Unit form',
                      caption: 'Create Business Unit: client, parent, name, code, description, active.',
                    },
                  ]}
                >
                  <Text>
                    Business units nest under a client (and optionally under another unit). Open{' '}
                    <strong>Business Units</strong> → Create / Edit.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Client *</strong> — Which tenant owns this unit.
                    </ListItem>
                    <ListItem>
                      <strong>Parent Business Unit</strong> — Leave “None” for a top-level unit, or pick a parent to
                      build a hierarchy.
                    </ListItem>
                    <ListItem>
                      <strong>Name *</strong> — Display name (for example Consumer Electronics).
                    </ListItem>
                    <ListItem>
                      <strong>Business Code *</strong> — Unique code within the client (for example CONSUMER_ELEC).
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — Optional free text.
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive units are hidden from most selectors (including Stores).
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Stores (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-03-stores-list.png',
                      alt: 'Stores list page',
                      caption: 'Stores list with Create Store.',
                    },
                    {
                      src: '/assets/img/help/org-03-stores-create.png',
                      alt: 'Create Store form with all fields',
                      caption:
                        'Create Store: Business Unit (Client), Business Unit, branch fields, region/province, contacts, phones, addresses, latitude/longitude, active.',
                    },
                  ]}
                >
                  <Text>
                    Stores (branches) belong to a client and optionally a business unit. Open <strong>Stores</strong> →
                    Create / Edit. You can also open <strong>View Details</strong> on a row for a read-only profile.
                  </Text>
                  <Heading as="h4" size="sm">
                    Identity &amp; hierarchy
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Business Unit (Client) *</strong> — Select the client first (label in the form).
                    </ListItem>
                    <ListItem>
                      <strong>Business Unit *</strong> — Unit under that client (required when units exist for the
                      client).
                    </ListItem>
                    <ListItem>
                      <strong>Branch Code *</strong> — Short store code (for example STR001).
                    </ListItem>
                    <ListItem>
                      <strong>Branch Name *</strong> — Display name (for example SM North EDSA).
                    </ListItem>
                    <ListItem>
                      <strong>Branch Address</strong> — Primary street / mall address.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Location &amp; contact
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Region</strong> / <strong>Province</strong> — Pick from location master data (maintain
                      Regions / Provinces first if lists are empty).
                    </ListItem>
                    <ListItem>
                      <strong>Branch Email</strong> — Optional store email.
                    </ListItem>
                    <ListItem>
                      <strong>Contact Person</strong> — Optional on-site contact name.
                    </ListItem>
                    <ListItem>
                      <strong>Contact Number</strong> / <strong>Mobile Number</strong> — At least one phone is typically
                      required (form helper: each is required if the other is empty).
                    </ListItem>
                    <ListItem>
                      <strong>Location Address</strong> / <strong>Normalized Address</strong> — Optional extra or
                      geocoded address lines.
                    </ListItem>
                    <ListItem>
                      <strong>Latitude</strong> / <strong>Longitude</strong> — Optional map coordinates for the store
                      (separate fields).
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive stores are hidden from most workflows.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Location master data (clusters, regions, provinces, towns)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-08-towns-list.png',
                      alt: 'Towns list page',
                      caption: 'Towns list (location master data). Area Clusters / Regions / Provinces look similar.',
                    },
                    {
                      src: '/assets/img/help/org-08-towns-create.png',
                      alt: 'Create Town form',
                      caption:
                        'Create Town: Code, Town Name, Province, Active. Other geo entities use Code + Name + parent link + Active.',
                    },
                  ]}
                >
                  <Text>
                    Maintain geography in order: <strong>Area Clusters</strong> → <strong>Geographic Regions</strong> →{' '}
                    <strong>Provinces</strong> → <strong>Towns</strong>. Stores and field coverage depend on these
                    lists.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Code *</strong> — Short unique code for the record.
                    </ListItem>
                    <ListItem>
                      <strong>Name</strong> (Area Cluster / Region) or <strong>Province Name</strong> /{' '}
                      <strong>Town Name</strong> — Display label (* required).
                    </ListItem>
                    <ListItem>
                      <strong>Area Cluster</strong> — On Regions (and often Provinces): which cluster this belongs to.
                    </ListItem>
                    <ListItem>
                      <strong>Region</strong> — On Provinces: parent region (may filter by cluster).
                    </ListItem>
                    <ListItem>
                      <strong>Province *</strong> — On Towns: parent province.
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive rows are hidden from selectors.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Create parents before children so dropdowns are populated when you add stores or field engineers.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. Departments &amp; Teams (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-04-departments-list.png',
                      alt: 'Departments list page',
                      caption: 'Departments list with Create Department.',
                    },
                    {
                      src: '/assets/img/help/org-04-departments-create.png',
                      alt: 'Create Department form',
                      caption: 'Create Department: name, slug, code, parent, description, active.',
                    },
                    {
                      src: '/assets/img/help/org-05-teams-list.png',
                      alt: 'Teams list page',
                      caption: 'Teams list with Create Team.',
                    },
                    {
                      src: '/assets/img/help/org-05-teams-create.png',
                      alt: 'Create Team form',
                      caption: 'Create Team: name, description, active.',
                    },
                  ]}
                >
                  <Heading as="h4" size="sm">
                    Department fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name *</strong> — Department title (for example Information Technology).
                    </ListItem>
                    <ListItem>
                      <strong>Slug</strong> — URL-safe key; leave empty to auto-generate from name.
                    </ListItem>
                    <ListItem>
                      <strong>Department Code</strong> — Optional code; leave empty to auto-generate.
                    </ListItem>
                    <ListItem>
                      <strong>Parent Department</strong> — Optional hierarchy; “None” for top-level.
                    </ListItem>
                    <ListItem>
                      <strong>Descriptions</strong> — Optional notes.
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive departments are hidden from the Users form selector.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Team fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name *</strong> — Team label (for example JFC Helpdesk 1).
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — Optional (max ~2000 characters).
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive teams are hidden from User → Team Assignment.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    7. Field Engineers (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-06-field-engineers-list.png',
                      alt: 'Field Engineers list page',
                      caption: 'Field Engineers list with Create Field Engineer.',
                    },
                    {
                      src: '/assets/img/help/org-06-field-engineers-create.png',
                      alt: 'Create Field Engineer form with all fields',
                      caption:
                        'Field Engineer form: link user, name, coverage, address, longitude/latitude, launch coords, comments, certifications block.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Field Engineers</strong> → Create / Edit for field staff profiles used in dispatch and
                    coverage.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Link User</strong> — Search and select an Auth user (optional). When linked, area cluster
                      can come from that user.
                    </ListItem>
                    <ListItem>
                      <strong>Full Name *</strong> — Engineer display name.
                    </ListItem>
                    <ListItem>
                      <strong>Province</strong> / <strong>Region</strong> — Coverage text (as used by your process).
                    </ListItem>
                    <ListItem>
                      <strong>Launch Point *</strong> — Home / start city or hub (for example CAGAYAN DE ORO CITY).
                    </ListItem>
                    <ListItem>
                      <strong>Address *</strong> — Full street address.
                    </ListItem>
                    <ListItem>
                      <strong>Normalized Address</strong> — Optional cleaned / geocoded address.
                    </ListItem>
                    <ListItem>
                      <strong>Longitude</strong> / <strong>Latitude</strong> — Optional map point for the person
                      (separate fields).
                    </ListItem>
                    <ListItem>
                      <strong>Launch Longitude</strong> / <strong>Launch Latitude</strong> — Optional map point for the
                      launch hub.
                    </ListItem>
                    <ListItem>
                      <strong>Comments</strong> — Free-text remarks.
                    </ListItem>
                    <ListItem>
                      <strong>Certifications</strong> — Expand/add certification rows with:
                      <UnorderedList mt={2} spacing={1}>
                        <ListItem>
                          <strong>Certification Name *</strong>
                        </ListItem>
                        <ListItem>
                          <strong>Certification Type</strong>
                        </ListItem>
                        <ListItem>
                          <strong>Issued Date</strong> / <strong>Expiry Date</strong>
                        </ListItem>
                        <ListItem>
                          <strong>Issuing Organization</strong>
                        </ListItem>
                        <ListItem>
                          <strong>Certificate Number</strong>
                        </ListItem>
                        <ListItem>
                          <strong>Notes</strong>
                        </ListItem>
                      </UnorderedList>
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    8. Client Purchase Orders (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/org-07-purchase-orders-list.png',
                      alt: 'Client Purchase Orders list page',
                      caption: 'Purchase Orders list with Create Purchase Order.',
                    },
                    {
                      src: '/assets/img/help/org-07-purchase-orders-create.png',
                      alt: 'Create Purchase Order form',
                      caption:
                        'Purchase Order form: client, PO number, service area, portfolio, amount, description, sales person, start/end dates, duration.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Client Purchase Orders</strong> → Create / Edit to record contract / PO periods per
                    client.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Client *</strong> — Which client owns the PO.
                    </ListItem>
                    <ListItem>
                      <strong>PO Number *</strong> — Contract / PO reference (for example CPO-2026-001).
                    </ListItem>
                    <ListItem>
                      <strong>Service Area</strong> — Optional business/contract label (not an email domain).
                    </ListItem>
                    <ListItem>
                      <strong>Portfolio</strong> — Optional portfolio name.
                    </ListItem>
                    <ListItem>
                      <strong>Amount</strong> — Optional monetary amount.
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — Optional PO notes.
                    </ListItem>
                    <ListItem>
                      <strong>Sales Person</strong> — Optional owner name.
                    </ListItem>
                    <ListItem>
                      <strong>Start Date *</strong> / <strong>End Date *</strong> — Contract window.
                    </ListItem>
                    <ListItem>
                      <strong>Duration</strong> — Read-only; calculated automatically from the two dates.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    9. Agent Skills
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/org-09-agent-skills.png',
                    alt: 'Agent Skills matrix for a client',
                    caption: 'Agent Skills: pick client, set Can Handle, Priority, Skill Level, Daily Capacity, Save.',
                  }}
                >
                  <Text>
                    <strong>Agent Skills</strong> is a matrix (not a single create form). Choose a client, optionally
                    filter by role, then set skills and <strong>Save</strong>.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Client</strong> — Load agents assigned for that client’s ticket handling.
                    </ListItem>
                    <ListItem>
                      <strong>Role filter</strong> — Narrow to Senior Team Lead, Team Lead, Service Desk, Field
                      Engineer, etc.
                    </ListItem>
                    <ListItem>
                      <strong>Can Handle</strong> — Whether this agent may take work for the client.
                    </ListItem>
                    <ListItem>
                      <strong>Priority</strong> — Primary / Secondary / Backup routing preference.
                    </ListItem>
                    <ListItem>
                      <strong>Skill Level</strong> — Relative skill rating (typically 1–5).
                    </ListItem>
                    <ListItem>
                      <strong>Daily Capacity</strong> — How many items they can handle per day (process-specific).
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Export / import may be available from the page toolbar when your role allows it.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* Settings — detailed accordion (excludes Content Push Console & Module) */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            Settings
          </Heading>
          <Text color={textSecondary} mb={4}>
            System settings control security, email, navigation, applications, backups, and related admin tools. Expand
            a topic for steps and field-by-field details. Change these carefully — they affect every user. This manual
            does not cover Content Push Console or Module pages.
          </Text>

          <Accordion allowMultiple defaultIndex={[0, 1]} reduceMotion>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. What Settings covers
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <VStack align="stretch" spacing={3} maxW="720px">
                  <Text>
                    Open the <strong>Settings</strong> (or System Settings) group in the sidebar. Typical pages:
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>General</strong>, <strong>Security</strong>, <strong>Email Settings</strong> — site-wide
                      preferences and outbound mail.
                    </ListItem>
                    <ListItem>
                      <strong>Email Templates</strong> — notification copy used across the system.
                    </ListItem>
                    <ListItem>
                      <strong>Applications</strong>, <strong>App Versions</strong> — registered products and mobile
                      release rollout.
                    </ListItem>
                    <ListItem>
                      <strong>Navigation</strong> — sidebar menu tree (backend-driven routes).
                    </ListItem>
                    <ListItem>
                      <strong>Backup &amp; Restore</strong> — manual backups and schedules.
                    </ListItem>
                    <ListItem>
                      <strong>Device Health</strong> — live mobile connectivity overview.
                    </ListItem>
                    <ListItem>
                      <strong>Mailbox / Graph subscriptions</strong> — Microsoft Graph webhooks for email intake.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    List pages usually share All / Trash tabs, search, bulk actions, and Create when you have
                    permission.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. General Settings (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/set-01-general.png',
                    alt: 'General Settings form',
                    caption: 'General Settings: Timezone, Date Format, Time Format.',
                  }}
                >
                  <Text>
                    Open <strong>General</strong> to set how dates and times appear across Auth. Click{' '}
                    <strong>Save Settings</strong> when done.
                  </Text>
                  <Heading as="h4" size="sm">
                    Date &amp; Time Settings
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Timezone</strong> — Default IANA timezone for the application (for example Asia/Manila).
                    </ListItem>
                    <ListItem>
                      <strong>Date Format</strong> — How dates display (YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, or Month Day,
                      Year).
                    </ListItem>
                    <ListItem>
                      <strong>Time Format</strong> — 24-hour or 12-hour clock display.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Security Settings (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/set-02-security.png',
                    alt: 'Security Settings form',
                    caption: 'Security: 2FA, session timeout, max login attempts, lockout duration.',
                  }}
                >
                  <Text>
                    Open <strong>Security</strong> to configure authentication and session policies. Save carefully —
                    lockouts and 2FA affect every login.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>2FA Enabled</strong> — Allow users to enroll in two-factor authentication.
                    </ListItem>
                    <ListItem>
                      <strong>2FA Required</strong> — Force all users to complete 2FA (only applies if 2FA is enabled).
                    </ListItem>
                    <ListItem>
                      <strong>Backup Codes Count</strong> — How many one-time backup codes to generate per user (typically
                      5–20).
                    </ListItem>
                    <ListItem>
                      <strong>Enable Session Timeout</strong> — When off, sessions can remain active indefinitely.
                    </ListItem>
                    <ListItem>
                      <strong>Session Timeout (minutes)</strong> — Idle minutes before logout (for example 5–1440).
                      Disabled when session timeout is off.
                    </ListItem>
                    <ListItem>
                      <strong>Max Login Attempts</strong> — Failed sign-ins before lockout (for example 3–10).
                    </ListItem>
                    <ListItem>
                      <strong>Lockout Duration (minutes)</strong> — How long an account stays locked after max attempts
                      (for example 5–60).
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Email Settings (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/set-03-email-settings.png',
                    alt: 'Email Settings form with SMTP fields',
                    caption: 'Email Settings: service provider, From address, and SMTP credentials (example).',
                  }}
                >
                  <Text>
                    Open <strong>Email Settings</strong> to choose the outbound mail provider and From identity. Fields
                    under the provider section change based on <strong>Email Service</strong>.
                  </Text>
                  <Heading as="h4" size="sm">
                    Always visible
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Email Service</strong> — SMTP, Mailgun, Postmark, Amazon SES, Microsoft Graph / Office
                      365, Sendmail, or testing drivers (Log / Array).
                    </ListItem>
                    <ListItem>
                      <strong>From Name</strong> — Display name in the From field (for example Your App Name).
                    </ListItem>
                    <ListItem>
                      <strong>From Email Address</strong> — Address shown as the sender (for example
                      noreply@example.com).
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    When Email Service = SMTP
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>SMTP Host</strong> / <strong>SMTP Port</strong> — Server and port (common: 587 TLS, 465
                      SSL, 25).
                    </ListItem>
                    <ListItem>
                      <strong>Encryption</strong> — None, TLS, SSL, or STARTTLS.
                    </ListItem>
                    <ListItem>
                      <strong>Username</strong> / <strong>Password</strong> — SMTP credentials.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Other providers (fields appear when selected)
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Mailgun</strong> — Mailgun Domain, Mailgun Secret Key.
                    </ListItem>
                    <ListItem>
                      <strong>Postmark</strong> — Postmark Server API Token.
                    </ListItem>
                    <ListItem>
                      <strong>Amazon SES</strong> — AWS Access Key ID, Secret Access Key, Region.
                    </ListItem>
                    <ListItem>
                      <strong>Microsoft Graph / Office 365</strong> — Tenant ID, Client ID (Application ID), Client
                      Secret, Sender Email Address.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Click <strong>Save</strong> / <strong>Save Email Settings</strong> after changes. Test with a known
                    template before relying on password resets or tickets.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Email Templates (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/set-04-email-templates-list.png',
                      alt: 'Email Templates list',
                      caption: 'Email Templates list: Name, Type, Subject, Receiver, Status.',
                    },
                    {
                      src: '/assets/img/help/set-04-email-templates-create.png',
                      alt: 'Create Email Template form',
                      caption:
                        'Create Email Template: Name, Type, Receiver, CC, Subject, Description, HTML Body, Active.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Email Templates</strong> → <strong>Create</strong> (or Edit) to manage notification
                    copy.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Template Name *</strong> — Friendly label (for example Welcome Email).
                    </ListItem>
                    <ListItem>
                      <strong>Template Type *</strong> — Unique code used by the system (for example welcome_email).{' '}
                      <strong>Cannot be changed after create</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Default Receiver Email</strong> — Optional default recipient (can be overridden when
                      sending).
                    </ListItem>
                    <ListItem>
                      <strong>CC Email Addresses</strong> — Comma-separated CC list.
                    </ListItem>
                    <ListItem>
                      <strong>Email Subject *</strong> — Subject line; may include placeholders such as{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        [recipient_name]
                      </Text>
                      .
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — When this template is used.
                    </ListItem>
                    <ListItem>
                      <strong>Email Body (HTML) *</strong> — HTML content with placeholders like{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        [recipient_name]
                      </Text>
                      ,{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        [request_number]
                      </Text>
                      ,{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        [link]
                      </Text>
                      .
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive templates are not selected for sending.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. Applications (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/set-05-applications-list.png',
                      alt: 'Applications list',
                      caption: 'Applications list: Name, Code, Description, Status.',
                    },
                    {
                      src: '/assets/img/help/set-05-applications-create.png',
                      alt: 'Create Application form',
                      caption: 'Create Application: Name, Code, Description, Active.',
                    },
                  ]}
                >
                  <Text>
                    Applications are the web/mobile products users can access (shown on the Users form under Web App
                    Access). Open <strong>Applications</strong> → Create / Edit.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name *</strong> — Display name (for example Auth Web App).
                    </ListItem>
                    <ListItem>
                      <strong>Code *</strong> — Short unique code (for example auth).{' '}
                      <strong>Cannot be changed after create</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — Optional notes.
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — When inactive, hidden from selection (including user app access).
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    7. App Versions (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/set-06-app-versions-list.png',
                      alt: 'App Versions list',
                      caption: 'App Versions list: Application, Version, Build, Mandatory, Rollout %, Published.',
                    },
                    {
                      src: '/assets/img/help/set-06-app-versions-create.png',
                      alt: 'Create App Version form',
                      caption:
                        'Create App Version: Application, Version, Build, Min Supported, Rollout %, Download URL, Checksum, Mandatory, Published At, Release Notes.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>App Versions</strong> to publish mobile release metadata and rollout. Create / Edit:
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Application *</strong> — Which registered application this release belongs to.
                    </ListItem>
                    <ListItem>
                      <strong>Version *</strong> — Semantic version (for example 1.2.0).
                    </ListItem>
                    <ListItem>
                      <strong>Build</strong> — Build number (for example 120).
                    </ListItem>
                    <ListItem>
                      <strong>Min Supported Version</strong> — Oldest client version still allowed.
                    </ListItem>
                    <ListItem>
                      <strong>Rollout %</strong> — Percentage of devices that should receive this release (0–100).
                    </ListItem>
                    <ListItem>
                      <strong>Download URL *</strong> — Where the binary / package is fetched.
                    </ListItem>
                    <ListItem>
                      <strong>Checksum</strong> — Optional integrity hash.
                    </ListItem>
                    <ListItem>
                      <strong>Mandatory Update</strong> — When on, clients must upgrade.
                    </ListItem>
                    <ListItem>
                      <strong>Published At</strong> — Publish datetime.
                    </ListItem>
                    <ListItem>
                      <strong>Release Notes</strong> — What changed in this build.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    8. Navigation (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/set-07-navigation-list.png',
                      alt: 'Navigation list',
                      caption: 'Navigation list: Name, Slug, Status.',
                    },
                    {
                      src: '/assets/img/help/set-07-navigation-create.png',
                      alt: 'Create Navigation form',
                      caption:
                        'Create Navigation: Name, Slug, Icon, Parent, Active, Show in Menu, Application.',
                    },
                  ]}
                >
                  <Text>
                    Navigation drives the sidebar (and must align with frontend route registry entries). Open{' '}
                    <strong>Navigation</strong> → Create / Edit.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name *</strong> — Menu label shown to users.
                    </ListItem>
                    <ListItem>
                      <strong>Slug *</strong> — Path key (for example system-settings/navigation).
                    </ListItem>
                    <ListItem>
                      <strong>Icon</strong> — Icon name used by the sidebar (for example settings, menu).
                    </ListItem>
                    <ListItem>
                      <strong>Parent Navigation</strong> — None (root) or a parent to nest under a group.
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive items are not used in the tree.
                    </ListItem>
                    <ListItem>
                      <strong>Show in Menu</strong> — Whether the item appears in the sidebar.
                    </ListItem>
                    <ListItem>
                      <strong>Application</strong> — Optional scope: None (Shared), Auth, SIMA, or Support.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    After changing navigation, users may need to sign out and sign in so menus refresh. Role permissions
                    still control which items each person can open.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    9. Backup &amp; Restore (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/set-08-backup-list.png',
                      alt: 'Backup and Restore list',
                      caption: 'Backup & Restore: stats and backup history with Create Backup.',
                    },
                    {
                      src: '/assets/img/help/set-08-backup-create.png',
                      alt: 'Create Backup form',
                      caption: 'Create Backup: Name, Type, Database, Compression, Storage, Encrypt, Retention.',
                    },
                    {
                      src: '/assets/img/help/set-08-schedules-list.png',
                      alt: 'Backup Schedules list',
                      caption: 'Backup Schedules list: Name, Type, Frequency, Status, Next/Last Run.',
                    },
                    {
                      src: '/assets/img/help/set-08-schedules-create.png',
                      alt: 'Create Backup Schedule form',
                      caption:
                        'Create Backup Schedule: Name, Type, Frequency, Time, Compression, Storage, Encrypt, Retention, Active.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Backup &amp; Restore</strong> to create manual backups, download/restore completed
                    ones, and manage schedules.
                  </Text>
                  <Heading as="h4" size="sm">
                    Create Backup fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Backup Name *</strong> — Label for this run.
                    </ListItem>
                    <ListItem>
                      <strong>Backup Type</strong> — Database Only, Files Only, or Full (Database + Files).
                    </ListItem>
                    <ListItem>
                      <strong>Database</strong> — Which database to include (when type is Database or Full). Leave
                      default unless you know otherwise.
                    </ListItem>
                    <ListItem>
                      <strong>Compression</strong> — None, Gzip, or Zip.
                    </ListItem>
                    <ListItem>
                      <strong>Storage Disk</strong> — Local Storage or S3 Storage.
                    </ListItem>
                    <ListItem>
                      <strong>Encrypt Backup</strong> — Optional encryption for the archive.
                    </ListItem>
                    <ListItem>
                      <strong>Select Tables</strong> — Optional; leave empty to include all tables (Database/Full).
                    </ListItem>
                    <ListItem>
                      <strong>Retention Days</strong> — Optional auto-cleanup age (for example 30).
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Schedule fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Schedule Name *</strong> — Label for the job.
                    </ListItem>
                    <ListItem>
                      <strong>Backup Type</strong> / <strong>Compression</strong> / <strong>Storage Disk</strong> /
                      Encrypt / Retention — Same ideas as manual backup.
                    </ListItem>
                    <ListItem>
                      <strong>Frequency</strong> — Daily, Weekly, Monthly, or Custom (Cron Expression).
                    </ListItem>
                    <ListItem>
                      <strong>Time</strong> — Run time (when not using custom cron).
                    </ListItem>
                    <ListItem>
                      <strong>Day of Week</strong> / <strong>Day of Month</strong> — Shown for Weekly / Monthly.
                    </ListItem>
                    <ListItem>
                      <strong>Cron Expression</strong> — For Custom (for example{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        0 2 * * *
                      </Text>{' '}
                      = daily at 2 AM).
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive schedules do not run.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Restore is available on completed backups only — confirm the dialog; restores overwrite data and
                    should be done by administrators who understand the impact.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    10. Device Health
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/set-09-device-health.png',
                    alt: 'Device Health overview',
                    caption: 'Device Health: Online, Idle, Offline, and Total mobile connectivity stats.',
                  }}
                >
                  <Text>
                    Open <strong>Device Health</strong> for a live overview of field-app device connectivity. There is
                    no create form here — use Mobile Devices under User Management for per-device actions.
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Online</strong> — Devices currently connected.
                    </ListItem>
                    <ListItem>
                      <strong>Idle</strong> — Registered but not actively connected.
                    </ListItem>
                    <ListItem>
                      <strong>Offline</strong> — Not reachable.
                    </ListItem>
                    <ListItem>
                      <strong>Total</strong> — All tracked devices.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    11. Mailbox / Graph subscriptions (form fields)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/set-10-mailbox-list.png',
                      alt: 'Mailbox subscriptions list',
                      caption:
                        'Graph mailbox subscriptions: Mailbox, Subscription ID, Webhook URL, Resource, Active, Expires.',
                    },
                    {
                      src: '/assets/img/help/set-10-mailbox-create.png',
                      alt: 'Add subscription form',
                      caption: 'Add subscription: Mailbox (UPN/email) and optional Notification / webhook URL.',
                    },
                  ]}
                >
                  <Text>
                    Open <strong>Mailbox / Graph subscriptions</strong> to manage Microsoft Graph webhooks used for
                    email intake (new inbox messages). Use Renew when a subscription is near expiry.
                  </Text>
                  <Heading as="h4" size="sm">
                    Add subscription fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Mailbox (UPN / email) *</strong> — Mailbox to watch (for example helpdesk@contoso.com).
                      Subscribes to new inbox messages (change type created).
                    </ListItem>
                    <ListItem>
                      <strong>Notification / webhook URL</strong> — Optional HTTPS endpoint. Leave blank to use the
                      server default (<Text as="span" fontFamily="mono" fontSize="sm">MICROSOFT_WEBHOOK_URL</Text>).
                      Must be HTTPS and allowed by host rules.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Edit views are typically read-only for Graph IDs and stored URL/resource — to change mailbox or
                    webhook, delete and recreate. List actions include Renew, Delete, and Renew all (near expiry).
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* Asset Catalog — client-scoped workspace */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            Asset Catalog
          </Heading>
          <Text color={textSecondary} mb={4}>
            Per-client reference data (categories, brands, models, hardware/software specs, tags) used by store
            inventory and Support ticket assets. Opened from Organization → Clients — not a global sidebar module.
            Expand a topic for steps and screenshots.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]} reduceMotion>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. Open Manage Asset Catalog from a client
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/ac-01-clients-manage-action.png',
                    alt: 'Clients list with Manage Asset Catalog action',
                    caption:
                      'Organization → Clients: row Actions → Manage Asset Catalog (client must have a provisioned database). Sidebar shows Organization navigation.',
                  }}
                >
                  <Text>
                    Asset Catalog is scoped to one client database. You never pick the client inside the catalog —
                    you choose it here first.
                  </Text>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      Open <strong>Organization → Clients</strong> in the sidebar.
                    </ListItem>
                    <ListItem>
                      Find a client whose <strong>Database</strong> is provisioned (Create Database is finished).
                    </ListItem>
                    <ListItem>
                      Open the row <strong>Actions</strong> menu → <strong>Manage Asset Catalog</strong>.
                    </ListItem>
                    <ListItem>
                      You land on that client&apos;s catalog <strong>Overview</strong>. The client name/code badge is
                      fixed for the session in this workspace.
                    </ListItem>
                  </OrderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Clients without a database do not show Manage Asset Catalog. Provision the client DB first.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Workspace layout (Overview)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: '/assets/img/help/ac-02-overview.png',
                    alt: 'Asset Catalog Overview with left catalog menu',
                    caption:
                      'Manage Asset Catalog workspace: app header + sidebar; left Catalog sub-navigation (Overview, Categories, Functions, Brands, Models, RAM, Disk Space, MS Office, Software Providers, CPU, Processor, OS Version, Tags); client badge; Overview checklist.',
                  }}
                >
                  <Text>
                    The workspace is two columns under the Auth shell (main sidebar stays available):
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Header</strong> — title <em>Manage Asset Catalog</em> and a read-only badge for the
                      current client (name and code).
                    </ListItem>
                    <ListItem>
                      <strong>Left Catalog menu</strong> — Overview, Categories, Functions, Brands, Models, each
                      spec type (RAM, Disk Space, MS Office, Software Providers, CPU, Processor, OS Version), and
                      Tags. Use <strong>Back to Clients</strong> at the bottom to leave.
                    </ListItem>
                    <ListItem>
                      <strong>Overview</strong> — setup checklist and progress for this client; click a card or
                      Continue to jump to the next incomplete module.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Recommended order: Categories → Functions → Brands → Models → Spec options → Tags.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Categories and Functions
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/ac-03-categories-list.png',
                      alt: 'Categories list in Asset Catalog workspace',
                      caption:
                        'Categories list: left Catalog menu with Categories selected; All/Trash; Create Category; columns Name, Slug, Description, Status, Updated At, Actions.',
                    },
                    {
                      src: '/assets/img/help/ac-04-categories-create.png',
                      alt: 'Create Category form',
                      caption:
                        'Create Category: Name *, Slug * (auto from name), Description, Active; Cancel / Create Category. Left Catalog menu remains visible.',
                    },
                  ]}
                >
                  <Text>
                    <strong>Categories</strong> are machine types (POS Terminal, Printer, Kiosk).{' '}
                    <strong>Functions</strong> hang under a category (Printing under Printer).
                  </Text>
                  <Heading as="h4" size="sm">
                    Category form fields
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Name *</strong> — Display name (e.g. POS Terminal).
                    </ListItem>
                    <ListItem>
                      <strong>Slug *</strong> — URL-friendly id; auto-filled from Name, editable.
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong> — Optional notes.
                    </ListItem>
                    <ListItem>
                      <strong>Active</strong> — Inactive options are hidden from asset pickers.
                    </ListItem>
                  </UnorderedList>
                  <Text>
                    List pages use <strong>All / Trash</strong>, search, bulk Move to trash / Restore, and Create when
                    you have permission — same pattern as other Auth lists.
                  </Text>
                  <Heading as="h4" size="sm">
                    CSV Template, Import, and Export
                  </Heading>
                  <Text>
                    Each catalog list (Categories, Functions, Brands, Models, Specs, Tags) has per-module{' '}
                    <strong>Template</strong>, <strong>Import</strong>, and <strong>Export</strong> actions (same icons
                    as Clients), gated by <strong>can_import</strong> / <strong>can_export</strong>. Import upserts on
                    the module&apos;s natural key (e.g. Slug for Categories; Name + Brand + Category for Models).
                  </Text>
                  <Text>
                    Recommended import order (also written as comment lines in each template):{' '}
                    <strong>1) Categories → 2) Functions → 3) Brands → 4) Models → 5) Spec lists (any order) → 6)
                    Tags</strong>
                    . Missing parent Brand/Category on Functions or Models fails that row with a clear error — parents
                    are not auto-created.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Brands, Models, Specs, and Tags
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: '/assets/img/help/ac-05-brands-list.png',
                      alt: 'Brands list in Asset Catalog workspace',
                      caption:
                        'Brands list: Catalog menu with Brands selected; columns Name, Categories, Description, Status, Actions.',
                    },
                    {
                      src: '/assets/img/help/ac-06-brands-create.png',
                      alt: 'Create Brand form',
                      caption:
                        'Create Brand: Name *, Description, Categories (checkboxes for machine types), Active.',
                    },
                    {
                      src: '/assets/img/help/ac-07-spec-ram.png',
                      alt: 'RAM Options list',
                      caption:
                        'Spec example — RAM Options: Catalog menu highlights RAM; All/Trash; Name, Slug, Description, Status.',
                    },
                  ]}
                >
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Brands</strong> — Manufacturer name; link one or more <strong>Categories</strong>{' '}
                      (machine types) so asset forms can filter correctly.
                    </ListItem>
                    <ListItem>
                      <strong>Models</strong> — Specific model under a Brand + Category (e.g. Epson TM-T88VI /
                      Printer).
                    </ListItem>
                    <ListItem>
                      <strong>Spec options</strong> — Separate left-menu items for RAM, Disk Space, MS Office,
                      Software Providers, CPU, Processor, OS Version. Each is a simple name/slug/description list.
                    </ListItem>
                    <ListItem>
                      <strong>Tags</strong> — Optional labels for filtering and reporting.
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Support Admin attaches assets on parent tickets using this client&apos;s catalog — agents should
                    not re-maintain these lists in the helpdesk app.
                  </Text>
                  <Text fontSize="sm">
                    Use each module&apos;s CSV Template / Import / Export (see Categories and Functions above for import
                    order). Spec lists and Tags can be imported in any order after Categories/Brands/Models as needed.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </VStack>
    </BasePageLayout>
  )
}
